/**
 * Optimizer Control Center — small display cells extracted from the row
 * (150-line cap): country flag, last-run outcome, static tag.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';

import { Tooltip } from '@/components/ui';
import { staticTagProviderOf } from '@/features/campaigns/tracker/campaign-tracker-access';
import { CampaignTagControl } from '@/features/flow-optimizer/components/CampaignTagControl';
import type { OptimizerRow } from '@/features/optimizer-center/optimizer-center.types';
import { formatDateTimeGb } from '@/lib/utils';
import {
  countryToFlagEmoji,
  inferCountryNameFromText,
} from '@/utils/country-flag-emoji';

export const CountryCell: FC<{ row: OptimizerRow }> = ({ row }) => {
  // Prefer the structured country; for Binom rows without a geo rule, fall back
  // to a country embedded in the campaign name (e.g. "… - Australia - …").
  const country = row.country || inferCountryNameFromText(row.campaignName);
  return (
    <Tooltip content={country ?? 'Unknown country'}>
      <span className="text-base leading-none" aria-label={country ?? 'Unknown country'}>
        {countryToFlagEmoji(country)}
      </span>
    </Tooltip>
  );
};

export const LastRunCell: FC<{ row: OptimizerRow }> = ({ row }) => {
  if (!row.lastRunAt) return <span className="text-muted-foreground">never</span>;
  const icon =
    row.lastRunOk === false ? (
      <Tooltip content={row.lastError ?? 'Last run failed'}>
        <XCircle aria-label="Last run failed" className="h-3.5 w-3.5 text-destructive" />
      </Tooltip>
    ) : (
      <CheckCircle2 aria-label="Last run ok" className="h-3.5 w-3.5 text-success" />
    );
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      {formatDateTimeGb(row.lastRunAt)}
      {icon}
    </span>
  );
};

/** Static tags always carry the row’s own tracker, including imported IDs. */
export const RowTagCell: FC<{ row: OptimizerRow; canEdit: boolean }> = ({ row, canEdit }) => {
  const tagProvider = staticTagProviderOf(row.provider);
  if (!tagProvider) return <span className="text-muted-foreground">—</span>;
  return (
    <CampaignTagControl
      provider={tagProvider}
      campaignId={row.campaignId}
      tag={row.staticTag}
      canEdit={canEdit}
    />
  );
};
